import { AffectedOrder } from "../Domain/AffectedOrder";
import { MealOption } from "../Domain/MealSettings";

export interface DietPreferenceChange {
  name: string;
  status: "added" | "removed" | "unchanged";
}

/**
 * Get new diet preferences for an order based on its meal option
 */
export const getNewDietPreferences = (
  order: AffectedOrder,
  mealOptions: MealOption[]
): string[] => {
  const mealOption = mealOptions.find(
    (mo) => mo.mealOptionId === order.mealOptionId
  );
  return mealOption?.dietPreferences.map((dp) => dp.name) || [];
};

/**
 * Check if diet preferences changed for an order
 */
export const hasPreferencesChanged = (
  order: AffectedOrder,
  mealOptions: MealOption[]
): boolean => {
  const current = [...order.currentDietPreferences].sort();
  const newPrefs = [...getNewDietPreferences(order, mealOptions)].sort();
  return JSON.stringify(current) !== JSON.stringify(newPrefs);
};

/**
 * Categorize diet preferences into added, removed, and unchanged
 */
export const categorizeDietPreferences = (
  order: AffectedOrder,
  mealOptions: MealOption[]
): DietPreferenceChange[] => {
  const currentPrefs = new Set(order.currentDietPreferences);
  const newPrefs = new Set(getNewDietPreferences(order, mealOptions));
  const allPrefs = new Set([...Array.from(currentPrefs), ...Array.from(newPrefs)]);

  const changes: DietPreferenceChange[] = [];

  allPrefs.forEach((pref) => {
    const inCurrent = currentPrefs.has(pref);
    const inNew = newPrefs.has(pref);

    if (inCurrent && inNew) {
      changes.push({ name: pref, status: "unchanged" });
    } else if (inNew && !inCurrent) {
      changes.push({ name: pref, status: "added" });
    } else if (inCurrent && !inNew) {
      changes.push({ name: pref, status: "removed" });
    }
  });

  // Sort: added first, then unchanged, then removed
  return changes.sort((a, b) => {
    const order = { added: 0, unchanged: 1, removed: 2 };
    return order[a.status] - order[b.status];
  });
};

/**
 * Group orders by date
 */
export const groupOrdersByDate = (
  orders: AffectedOrder[]
): { [key: string]: AffectedOrder[] } => {
  return orders.reduce((acc, order) => {
    // Use local date to avoid timezone conversion
    const year = order.date.getFullYear();
    const month = String(order.date.getMonth() + 1).padStart(2, '0');
    const day = String(order.date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(order);
    return acc;
  }, {} as { [key: string]: AffectedOrder[] });
};

/**
 * Calculate appropriate delay for animation based on number of orders
 */
export const calculateAnimationDelay = (totalOrders: number): number => {
  const baseDelay = 50;
  const maxDelay = 150;
  return Math.max(baseDelay, Math.min(maxDelay, 3000 / totalOrders));
};
